import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { DoneAll, Info, InfoOutlined, RemoveDone } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { ProdutosContext } from 'contexts/ProdutosContext';
import Loader from 'components/Loader';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import Blank from 'assets/blank.png';

const ProdutoModal = ({ item }) => {
  const [tab, setTab] = useState(0);
  const [produto, setProduto] = useState(null);
  const [complemento, setComplemento] = useState(null);

  const { getURLRelatorio } = useContext(RelatoriosContext);
  const { getProdutoSaldo, getProdutoIndicadores, getLoading } =
    useContext(ProdutosContext);

  /* -------------------------------------------------------
      CALCULOS BÁSICOS (memoizados)
  ------------------------------------------------------- */
  const preco_total = useMemo(
    () =>
      (item?.preco || 0) * (item?.quantidade || 0) - (item?.desconto_vlr || 0),
    [item]
  );

  const comissaoValor = useMemo(
    () => preco_total * (item?.comissao_pc || 0),
    [preco_total, item?.comissao_pc]
  );

  const calcPercent = (value) =>
    preco_total > 0 ? ((value || 0) / preco_total) * 100 : 0;

  /* -------------------------------------------------------
      IMPOSTOS PRÉ-PROCESSADOS (memoizados)
  ------------------------------------------------------- */
  const impostos = useMemo(() => {
    const arr = {};
    const lista = item?.DocumentoItemImpostos || [];
    lista.forEach((imp) => {
      arr[imp.tributo_id] = {
        valor: imp.valor || 0,
        percentual: imp.percentual || 0,
        block: imp.margem === 'NAO',
      };
    });
    return arr;
  }, [item]);

  const imp = (id) => impostos[id] || { valor: 0, percentual: 0, block: false };

  /* -------------------------------------------------------
      FUNÇÃO DE TOTALIZAÇÃO (memoizada)
  ------------------------------------------------------- */
  const get_total = (arr) =>
    arr.reduce((acc, l) => {
      if (l.block) return acc;
      const v = l.invert ? (l.value || 0) * -1 : l.value || 0;
      return acc + v;
    }, 0);

  /* -------------------------------------------------------
      FORMULAÇÃO DAS LINHAS — custoLines & precoLines
      criados APENAS 1 VEZ (useMemo)
  ------------------------------------------------------- */
  const custoLines = useMemo(() => {
    const c = complemento?.indicadores?.formacaoCusto || {};

    return [
      { label: 'Preço:', value: c.custo },
      { label: 'Custo Médio:', value: c.medio, block: true },
      { label: 'Desconto:', value: c.desconto_vlr },
      { label: 'Comissão de Venda:', value: c.comissao_vlr },
      { label: 'Adicional (Vínculo):', value: c.adicional },
      { label: 'Frete:', value: c.frete },
      { label: 'Seguro:', value: c.seguro },
      { label: 'Despesas Acessórias:', value: c.despac },
      { label: 'Custo Financeiro:', value: c.cstfin },
      { label: 'Eventuais:', value: c.eventuais },
      { label: 'Outros:', value: c.outros },
      { label: 'Internacionalização:', value: c.internacional },
      { label: 'Frete (Não Destacado):', value: c.fretend },
      { label: 'Comissão (Não Destacada):', value: c.comissaond },
      {
        label: 'Processo de Fabricação:',
        value: c.processo,
        show: c.origem === 'FABRICADO',
      },
      {
        label: 'ICMS:',
        value: c.icms,
        block: !c.icms_calc,
        hint: 'Será somado ao custo se for uma importação. Será subtraido do custo se estiver configurado no sistema ou se a empresa não for optante pelo simples nacional e tiver direito ao crédito do imposto.',
      },
      {
        label: 'ICMS ST:',
        value: c.icms_st,
        block: !c.icms_st_calc,
        hint: 'Será somado ao custo se estiver configurado no sistema ou se a empresa não tiver direito ao crédito do imposto.',
      },
      {
        label: 'Diferença de ICMS ST:',
        value: c.diferenca_icms_st,
        block: !c.diferenca_icms_st_calc,
        hint: 'Será somado ao custo se estiver configurado no sistema.',
      },
      {
        label: 'IPI:',
        value: c.ipi,
        block: !c.ipi_calc,
        hint: 'Será somado ao custo se estiver configurado no sistema ou se a empresa não tiver direito ao crédito do imposto.',
      },
      {
        label: 'PIS:',
        value: c.pis,
        block: !c.pis_calc,
        hint: 'Será subtraido do custo se estiver configurado no sistema ou se o regime tributário da empresa for LUCRO REAL e a empresa tiver direito ao crédito do imposto.',
      },
      {
        label: 'COFINS:',
        value: c.cofins,
        block: !c.cofins_calc,
        hint: 'Será subtraido do custo se estiver configurado no sistema ou se o regime tributário da empresa for LUCRO REAL e a empresa tiver direito ao crédito do imposto.',
      },
      {
        label: 'Impostos Federais:',
        value: c.impostos_federais,
        block: !c.impostos_federais_calc,
        hint: 'Será subtraido do custo se estiver configurado no sistema ou se o regime tributário da empresa for LUCRO REAL e a empresa tiver direito ao crédito do imposto.',
      },
    ];
  }, [complemento]);

  const precoLines = useMemo(() => {
    const mc = item?.DocumentoItemMc || {};

    return [
      { label: 'Preço:', value: item?.preco },
      {
        label: 'Desconto:',
        value: item?.desconto_vlr,
        percentual: item?.desconto1_pc,
        invert: true,
      },
      {
        label: 'Comissão de Venda:',
        value: comissaoValor,
        percentual: item?.comissao_pc,
        invert: true,
      },
      {
        label: 'Frete:',
        value: item?.frete,
        percentual: calcPercent(item?.frete),
        invert: true,
      },
      {
        label: 'Custo Financeiro:',
        value: item?.cstfin,
        percentual: calcPercent(item?.cstfin),
        invert: true,
      },
      {
        label: 'Eventuais:',
        value: item?.eventuais,
        percentual: calcPercent(item?.eventuais),
        invert: true,
      },
      {
        label: 'Frete (Não Destacado):',
        value: mc?.fretend,
        percentual: calcPercent(mc?.fretend),
        invert: true,
      },
      {
        label: 'Comissão (Não Destacada):',
        value: mc?.comissaond,
        percentual: calcPercent(mc?.comissaond),
        invert: true,
      },
      {
        label: 'Impostos Federais (Não Destacado):',
        value: mc?.impfednd,
        percentual: calcPercent(mc?.impfednd),
        invert: true,
      },

      /* Impostos dinâmicos */
      ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((id) => ({
        label: [
          '',
          'ICMS',
          'ICMS ST',
          'IPI',
          'PIS',
          'COFINS',
          'CSLL',
          'INSS',
          'IRRF',
          'ISS',
          'FUNRURAL',
        ][id],
        value: imp(id).valor,
        percentual: imp(id).percentual,
        block: imp(id).block,
        invert: true,
      })),
    ];
  }, [item, imp, comissaoValor]);

  /* Cálculos de lucro */
  const lucroTotal = useMemo(() => {
    const totalPreco = get_total(precoLines);
    const totalCusto =
      item?.DocumentoItemMc?.custo > 0
        ? item?.DocumentoItemMc?.custo +
          (complemento?.indicadores?.formacaoCusto?.ipi_calc
            ? complemento.indicadores.formacaoCusto.ipi
            : 0)
        : get_total(custoLines);

    const seguroUnitario =
      item?.quantidade > 0
        ? (item?.DocumentoItemMc?.segurond || 0) / item?.quantidade
        : 0;

    return totalPreco - totalCusto - seguroUnitario;
  }, [precoLines, custoLines, item, complemento]);

  const percentualLucro = useMemo(() => {
    const p = get_total(precoLines);
    if (!p) return 0;
    return (lucroTotal / p) * 100;
  }, [lucroTotal, precoLines]);

  /* -------------------------------------------------------
      FETCH DOS DADOS
  ------------------------------------------------------- */
  useEffect(() => {
    getProdutoSaldo({ id: item?.produto_id, cb: setProduto });
    getProdutoIndicadores({ id: item?.produto_id, cb: setComplemento });
  }, []);

  if (getLoading) return <Loader />;

  /* -------------------------------------------------------
      COMPONENTES INTERNOS LIMPOS
  ------------------------------------------------------- */

  const Column = ({ rows, size }) => (
    <Box flex={size} display="flex" flexDirection="column">
      {rows.map((r, i) => (
        <Typography key={i} variant="caption" fontWeight="bold">
          {r.label}: <Typography variant="caption">{r.value || '-'}</Typography>
        </Typography>
      ))}
    </Box>
  );

  const Line = ({ label, value }) =>
    value ? (
      <Typography variant="caption" fontWeight="bold">
        {label}: <Typography variant="caption">{value}</Typography>
      </Typography>
    ) : null;

  const Formacao = ({ arr }) => (
    <Box my={2} gap={1} display="flex" flexDirection="column">
      {arr.map((l, i) => {
        if (l.show === false) return null;
        const value = l.invert ? (l.value || 0) * -1 : l.value || 0;

        return (
          <Box
            key={i}
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ color: l?.block && 'secondary.main' }}
          >
            <Typography variant="caption" fontWeight="bold">
              {l.label}
            </Typography>

            <Typography flex={1} textAlign="end" variant="caption">
              {value?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}
            </Typography>

            {l.percentual != null && (
              <Typography variant="caption">({l.percentual}%)</Typography>
            )}

            {l.hint && (
              <Tooltip title={l.hint}>
                <Info fontSize="small" color="disabled" />
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );

  /* -------------------------------------------------------
      RENDER
  ------------------------------------------------------- */
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} mx={2}>
        <Box display="flex" gap={2}>
          <Box flex={1} position="relative">
            <Chip
              size="small"
              label={produto?.ativo === 'SIM' ? 'ATIVO' : 'INATIVO'}
              color={produto?.ativo === 'SIM' ? 'primary' : 'error'}
              icon={produto?.ativo === 'SIM' ? <DoneAll /> : <RemoveDone />}
              sx={{ position: 'absolute', top: 10, left: 10 }}
            />
            <Box
              component="img"
              src={produto?.url || Blank}
              alt="Imagem"
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: 2,
                boxShadow: 3,
                objectFit: 'contain',
              }}
            />
          </Box>
          <Column
            size={1}
            rows={[
              { label: 'Referência', value: produto?.referencia },
              { label: 'Código', value: produto?.codigo },
              { label: 'Altura', value: produto?.altura },
              { label: 'Largura', value: produto?.largura },
              { label: 'Comprimento', value: produto?.comprimento },
              { label: 'Peso', value: produto?.peso },
              { label: 'Volume', value: produto?.volume },
            ]}
          />
          <Column
            size={2}
            rows={[
              { label: 'Grupo', value: produto?.grupo },
              { label: 'Linha', value: produto?.linha },
              { label: 'Marca', value: produto?.marca },
              { label: 'Modelo', value: produto?.modelo },
            ]}
          />
        </Box>
      </Grid>
      <Grid item xs={12} mx={2} display="flex" flexDirection="column">
        <Line label="NCM" value={produto?.ncm} />
        <Line label="Origem Tributária" value={produto?.origem_tributaria} />
        <Line label="Complemento 1" value={produto?.complemento1} />
        <Line label="Complemento 2" value={produto?.complemento2} />
      </Grid>
      <Grid item xs={12}>
        <Tabs variant="fullWidth" value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value={0} label="Saldo" />
          <Tab value={1} label="Compra" />
          <Tab value={2} label="Venda" />
        </Tabs>
      </Grid>
      {[1, 2].includes(tab) && (
        <Grid item xs={12} mx={2}>
          <Grid container spacing={2}>
            <Grid item xs={6} display="flex" flexDirection="column">
              <Card style={{ flex: 1 }}>
                <Typography fontWeight="bold">Custo da Compra</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {get_total(custoLines).toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                  {item?.quantidade > 0 && (
                    <Typography variant="caption" color="text.secondary" ml={1}>
                      x {item?.quantidade} {item?.UnidadeMedida?.sigla || ''} ={' '}
                      {(
                        get_total(custoLines) * item?.quantidade
                      ).toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </Typography>
                  )}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column">
              <Card style={{ flex: 1 }}>
                <Typography fontWeight="bold">Preço para Margem</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {get_total(precoLines).toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                  {item?.quantidade > 0 && (
                    <Typography variant="caption" color="text.secondary" ml={1}>
                      x {item?.quantidade} {item?.UnidadeMedida?.sigla || ''} ={' '}
                      {(
                        get_total(precoLines) * item?.quantidade
                      ).toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </Typography>
                  )}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column">
              <Card style={{ flex: 1 }}>
                <Typography fontWeight="bold">Valor do Lucro</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {lucroTotal.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                  {item?.quantidade > 0 && (
                    <Typography variant="caption" color="text.secondary" ml={1}>
                      x {item?.quantidade} {item?.UnidadeMedida?.sigla || ''} ={' '}
                      {(lucroTotal * item?.quantidade).toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </Typography>
                  )}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column">
              <Card style={{ flex: 1 }}>
                <Typography fontWeight="bold">% do Lucro</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {percentualLucro}%
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      )}
      {tab === 0 && (
        <Grid item xs={12} mx={2}>
          {produto?.estoque?.map((p, i) => {
            const rows = [
              ...p?.locais,
              { disponibilizar: 'NAO', local: 'Reserva', saldo: p?.reserva },
            ];
            return (
              <Box key={i} mb={2}>
                <Typography variant="body1" fontWeight="bold" mb={1}>
                  {p?.filial}
                </Typography>
                <DataGrid
                  rows={rows}
                  columns={[
                    {
                      field: 'local',
                      headerName: 'Local',
                      minWidth: 150,
                      flex: 1,
                      sortable: false,
                    },
                    {
                      field: 'disponibilizar',
                      headerName: 'Disponível',
                      width: 100,
                      sortable: false,
                      type: 'boolean',
                      valueGetter: ({ value }) => value === 'SIM',
                    },
                    {
                      field: 'saldo',
                      headerName: 'Saldo',
                      width: 150,
                      sortable: false,
                      renderCell: ({ row }) => (
                        <Box
                          display="flex"
                          flex={1}
                          gap={2}
                          alignItems="center"
                        >
                          <Typography variant="body2" flex={1}>
                            {row?.saldo || 0} {produto?.um}
                          </Typography>

                          <Tooltip title="Relatório">
                            <IconButton
                              size="small"
                              onClick={() =>
                                getURLRelatorio({
                                  data: {
                                    codigo: 'MVE0000001',
                                    prd: item?.produto_id,
                                    filial: p?.filial_id,
                                    local: row?.local_id,
                                  },
                                })
                              }
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ),
                    },
                  ]}
                  autoHeight
                  hideFooter
                  density="compact"
                  getRowId={(row) => JSON.stringify(row)}
                  disableSelectionOnClick
                  disableColumnMenu
                  showCellRightBorder
                  showColumnRightBorder
                  localeText={localeText}
                />
              </Box>
            );
          })}
        </Grid>
      )}
      {tab === 1 && (
        <Grid item xs={12} mx={2}>
          <Typography variant="body1" fontWeight="bold" textAlign="center">
            Formação do Custo
          </Typography>
          <Typography variant="caption" color="secondary" textAlign="center">
            * Valores não considerados na formação do custo.
          </Typography>
          <Formacao arr={custoLines} />
        </Grid>
      )}
      {tab === 2 && (
        <Grid item xs={12} mx={2}>
          <Typography variant="body1" fontWeight="bold" textAlign="center">
            Formação do Preço
          </Typography>
          <Typography variant="caption" color="secondary" textAlign="center">
            * Valores não considerados na formação do preço.
          </Typography>
          <Formacao arr={precoLines} />
        </Grid>
      )}
    </Grid>
  );
};

export default ProdutoModal;
